import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { Trade } from '../sim/types'

// Persistent trade journal (IndexedDB). Survives reloads and works offline.
// Trades accumulate across sessions — this is the durable record the synthetic
// per-session balance is NOT.

export interface JournalEntry extends Trade {
  timeframe: string
  source?: string
  createdAt: number
  rating?: number
  tags?: string[]
  setup?: string
  note?: string
  hasSnapshot?: boolean
}

interface JournalDB extends DBSchema {
  trades: {
    key: string
    value: JournalEntry
    indexes: { byExitTime: number; bySymbol: string }
  }
  snapshots: { key: string; value: { tradeId: string; png: Blob } }
  settings: { key: string; value: { key: string; value: unknown } }
}

const DB_NAME = 'backtest-journal'
const DB_VERSION = 1

let dbp: Promise<IDBPDatabase<JournalDB>> | null = null
function db() {
  if (!dbp) {
    dbp = openDB<JournalDB>(DB_NAME, DB_VERSION, {
      upgrade(d) {
        const trades = d.createObjectStore('trades', { keyPath: 'id' })
        trades.createIndex('byExitTime', 'exitTime')
        trades.createIndex('bySymbol', 'symbol')
        d.createObjectStore('snapshots', { keyPath: 'tradeId' })
        d.createObjectStore('settings', { keyPath: 'key' })
      },
    })
  }
  return dbp
}

export interface JournalStore {
  init(): Promise<void>
  addTrade(entry: JournalEntry): Promise<void>
  updateTrade(id: string, patch: Partial<JournalEntry>): Promise<void>
  deleteTrade(id: string): Promise<void>
  getTrades(): Promise<JournalEntry[]> // ascending by exitTime
  saveSnapshot(tradeId: string, png: Blob): Promise<void>
  getSnapshot(tradeId: string): Promise<Blob | undefined>
  clear(): Promise<void>
  exportJSON(): Promise<string>
  exportCSV(): Promise<string>
  importJSON(text: string, mode: 'merge' | 'replace'): Promise<number>
}

export function createJournalStore(): JournalStore {
  return {
    async init() {
      await db()
    },

    async addTrade(entry) {
      const d = await db()
      await d.put('trades', entry)
    },

    async updateTrade(id, patch) {
      const d = await db()
      const cur = await d.get('trades', id)
      if (!cur) return
      await d.put('trades', { ...cur, ...patch, id })
    },

    async deleteTrade(id) {
      const d = await db()
      const tx = d.transaction(['trades', 'snapshots'], 'readwrite')
      await tx.objectStore('trades').delete(id)
      await tx.objectStore('snapshots').delete(id)
      await tx.done
    },

    async getTrades() {
      const d = await db()
      return d.getAllFromIndex('trades', 'byExitTime')
    },

    async saveSnapshot(tradeId, png) {
      const d = await db()
      await d.put('snapshots', { tradeId, png })
    },

    async getSnapshot(tradeId) {
      const d = await db()
      const rec = await d.get('snapshots', tradeId)
      return rec?.png
    },

    async clear() {
      const d = await db()
      const tx = d.transaction(['trades', 'snapshots'], 'readwrite')
      await tx.objectStore('trades').clear()
      await tx.objectStore('snapshots').clear()
      await tx.done
    },

    async exportJSON() {
      const trades = await this.getTrades()
      return JSON.stringify({ schemaVersion: 1, exportedAt: Math.floor(Date.now() / 1000), trades }, null, 2)
    },

    async exportCSV() {
      const trades = await this.getTrades()
      const iso = (t: number) => new Date(t * 1000).toISOString()
      const cols = [
        'symbol',
        'timeframe',
        'direction',
        'size',
        'entryTime',
        'entryPrice',
        'exitTime',
        'exitPrice',
        'netPnL',
        'rMultiple',
        'exitReason',
        'rating',
        'setup',
        'tags',
        'note',
      ]
      const esc = (v: unknown) => {
        const s = v === undefined || v === null ? '' : String(v)
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
      }
      const rows = trades.map((t) =>
        [
          t.symbol,
          t.timeframe,
          t.direction,
          t.size,
          iso(t.entryTime),
          t.entryPrice,
          iso(t.exitTime),
          t.exitPrice,
          t.netPnL,
          t.rMultiple ?? '',
          t.exitReason,
          t.rating ?? '',
          t.setup ?? '',
          (t.tags ?? []).join(';'),
          t.note ?? '',
        ]
          .map(esc)
          .join(','),
      )
      return [cols.join(','), ...rows].join('\n')
    },

    async importJSON(text, mode) {
      const d = await db()
      const parsed = JSON.parse(text) as { trades?: JournalEntry[] }
      const trades = parsed.trades ?? []
      const tx = d.transaction('trades', 'readwrite')
      if (mode === 'replace') await tx.objectStore('trades').clear()
      for (const t of trades) await tx.objectStore('trades').put(t)
      await tx.done
      return trades.length
    },
  }
}
